"use client";
import { UNAUTHED_NAVBAR_LINKS } from "@/app/(landing)/constants";
import {
  BuyPageSearchParams,
  GpuInstanceTypeDropdown,
  useGPUInstanceType,
} from "@/components/buy-helpers";
import { EmptyState } from "@/components/empty-state";
import { MarketingText } from "@/components/marketing-text";
import { buttonVariants } from "@/components/ui/button";
import { INTERNAL_LINKS } from "@/lib/helpers/urls";
import { cn } from "cn";
import { ArrowRightCircleIcon } from "@heroicons/react/24/outline";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { LogoWithPressKit } from "./(press-kit)";
import styles from "./components.module.css";
import type { DataPoint } from "./prices/AvgMinMaxChart";
import AvgMinMaxChart from "./prices/AvgMinMaxChart";

export const TopBarClient = ({
  className,
  isSignedIn = false,
  signInUrl,
  signUpUrl,
}: {
  className?: string;
  isSignedIn?: boolean;
  signInUrl: string;
  signUpUrl: string;
}) => {
  const path = usePathname();
  const isFogEnabled = isFogEnabledForPath(path);

  return (
    <div className={cn("max-w-xl px-4 sm:px-0 mx-auto text-base", className)}>
      <div
        className={cn(
          "flex items-center justify-between text-neutral-500",
          isFogEnabled && "text-neutral-100",
        )}
      >
        <LogoWithPressKit />

        <div className="flex items-center gap-2 flex-wrap justify-end">
          {UNAUTHED_NAVBAR_LINKS.map((link) => (
            <Link
              key={link.href}
              prefetch={link.prefetch}
              href={link.href}
              className="underline hover:no-underline"
            >
              {link.label}
            </Link>
          ))}
          <LoginCta
            isSignedIn={isSignedIn}
            signInUrl={signInUrl}
            signUpUrl={signUpUrl}
          />
        </div>
      </div>
    </div>
  );
};

const primaryButtonClass = buttonVariants({ variant: "primary", size: "sm" });

const LoginCta = ({
  isSignedIn,
  signInUrl,
  signUpUrl,
}: {
  isSignedIn: boolean;
  signInUrl: string;
  signUpUrl: string;
}) => {
  const path = usePathname();
  const isFogEnabled = isFogEnabledForPath(path);

  if (isSignedIn) {
    return (
      <Link
        href={INTERNAL_LINKS.DASHBOARD.HOME}
        className={cn(
          styles.btnSmall,
          "text-base",
          isFogEnabled && "!text-neutral-900 !bg-white",
        )}
      >
        dashboard <ArrowRightCircleIcon className="w-4 h-4" />
      </Link>
    );
  }

  return (
    <div className="flex gap-2">
      <a
        href={signInUrl}
        className={cn(
          styles.btnSmall,
          "text-base",
          isFogEnabled && "!text-neutral-900 !bg-white",
        )}
      >
        login
      </a>
      <a
        href={signUpUrl}
        className={cn(
          primaryButtonClass,
          styles.btnSmall,
          "h-[revert] !text-white",
        )}
      >
        sign up
      </a>
    </div>
  );
};

export const PriceChart = function ({
  pricesByHardwareType,
}: {
  pricesByHardwareType: Record<string, DataPoint[]>;
}) {
  const [gpuType] = useGPUInstanceType();
  const prices = (pricesByHardwareType[gpuType] ?? [])
    .filter((p) => p.date >= new Date("2024-11-05")) // filtering this point to be the first point since when we started
    .sort((a, b) => b.date.getTime() - a.date.getTime());

  const averagePriceLabel =
    prices
      .filter((p) => p.avg !== 0)
      ?.at?.(0)
      ?.avg?.toFixed?.(2) ?? "-.--";

  return (
    <div className="pt-8 w-full h-[300px] mb-18">
      <MarketingText as="h2">
        Average prices for{" "}
        <GpuInstanceTypeDropdown pricesByHardwareType={pricesByHardwareType} />
      </MarketingText>
      <div className="flex gap-1 text-sm flex-col sm:flex-row sm:gap-2 justify-start">
        <div className="flex flex-row items-center gap-2">
          <div
            className={cn(
              "size-3.5 rounded-full bg-primary-gradient mr-0.5 translate-y-[0.5px]",
              averagePriceLabel === "-.--" && "bg-neutral-300",
            )}
          />
          <span className="text-neutral-800">${averagePriceLabel}</span>
          <span className="text-neutral-500">average gpu/hr</span>
        </div>
      </div>
      <AvgMinMaxChart
        data={prices}
        emptyState={
          <EmptyState
            className="my-4"
            icon="Leaderboard"
            title={`No historical ${gpuType} pricing data yet`}
            description={"Get a custom quote while we collect pricing data."}
            primaryAction={{
              label: `Get a ${gpuType} quote`,
              type: "link",
              href: `${INTERNAL_LINKS.BUY}?${BuyPageSearchParams.GPU_TYPE}=${gpuType}`,
            }}
          />
        }
      />
    </div>
  );
};

export const isFogEnabledForPath = (path: string) => {
  return path === INTERNAL_LINKS.CONTACT;
};
